import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Checkbox,
  Paper,
  MobileStepper,
  IconButton,
} from '@mui/material';
import { Mic as MicIcon, Add as AddIcon, Refresh as RefreshIcon, Assessment as StatsIcon, Campaign as CampaignIcon, ViewCarousel as CarouselIcon, AutoAwesome as AutoAwesomeIcon, ContentCopy as ContentCopyIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const POST_TYPES = [
  { value: 'offer', label: 'Offer' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'product_promotion', label: 'Product Promotion' },
  { value: 'service_promotion', label: 'Service Promotion' },
  { value: 'video_promotion', label: 'Video Promotion' },
  { value: 'coupon_promotion', label: 'Coupon Promotion' },
  { value: 'event_promotion', label: 'Event Promotion' },
];

const PLACEMENTS = [
  {
    value: 'popup',
    label: 'Popup ads (paid)',
    hint: 'Full-screen on app open · 1080×1920 (9:16)',
  },
  {
    value: 'home_pamphlets',
    label: 'Digital pamphlets (paid)',
    hint: 'Home carousel · thumb 1120×700 · full 1080×1920',
  },
  {
    value: 'shop_updates',
    label: 'Status / shop updates (free)',
    hint: 'Shop screen + Food Zone / Fresh Market rails · 800×500 thumb',
  },
  {
    value: 'my_town',
    label: 'My Town reels (paid)',
    hint: 'Town Pulse vertical reels · 1080×1920 (9:16)',
  },
  {
    value: 'category_featured',
    label: 'Category featured (paid)',
    hint: 'Category browse (e.g. Fresh Market) · 800×500 thumb',
  },
];

const FLUTTER_AD_TYPES = [
  ['Event Ads', 'Today and upcoming festivals — wish or offer ads', 'event_promotion'],
  ['New shop / showroom opening', 'Location, date, inauguration, opening offers', 'announcement'],
  ['Percentage (%) coupon', 'Percent off, coupon code, minimum purchase, validity', 'coupon_promotion'],
  ['Flat Discount (Rs.) coupon', 'Flat Rs. off, coupon code, minimum purchase, validity', 'coupon_promotion'],
  ['Buy X Get Y coupon', 'Buy product X, get product Y free, code', 'coupon_promotion'],
  ['Clearance sale', 'Discount banners for stock clearance', 'offer'],
  ['Product promotion', 'Highlight a product with photo or video', 'product_promotion'],
  ['Service promotion', 'Promote your service and special offer', 'service_promotion'],
];
// Local phonetic helpers keep ad-copy keystrokes on the merchant device.
const PHONETIC_WORDS = {
  te: { offer: 'ఆఫర్', discount: 'డిస్కౌంట్', sale: 'సేల్', free: 'ఫ్రీ', today: 'నేడు', festival: 'ఫెస్టివల్', service: 'సర్వీస్', shop: 'షాప్', opening: 'ఓపెనింగ్', jacket: 'జాకెట్', jackets: 'జాకెట్లు', blouse: 'బ్లౌజ్', blouses: 'బ్లౌజెస్', clearance: 'క్లియరెన్స్', maa: 'మా', vadda: 'వద్ద', vividha: 'వివిధ', rakala: 'రకాల', matching: 'మ్యాచింగ్', blouses: 'బ్లౌజెస్', pattu: 'పట్టు', mangalagiri: 'మంగళగిరి', benarasi: 'బనారసీ', ikkat: 'ఇక్కత్', kalankari: 'కలంకారి', modal: 'మోడల్', mukkalu: 'ముక్కలు', mariyu: 'మరియు', labhinchunu: 'లభించును' },
  hi: { offer: 'ऑफर', discount: 'डिस्काउंट', sale: 'सेल', free: 'फ्री', today: 'आज', festival: 'फेस्टिवल', service: 'सर्विस', shop: 'शॉप', opening: 'ओपनिंग' },
};
const TELUGU_VOWELS = { a: 'అ', aa: 'ఆ', i: 'ఇ', ee: 'ఈ', ii: 'ఈ', u: 'ఉ', oo: 'ఊ', uu: 'ఊ', e: 'ఎ', ae: 'ఏ', ai: 'ఐ', o: 'ఒ', oa: 'ఓ', au: 'ఔ' };
const TELUGU_MATRAS = { a: '', aa: 'ా', i: 'ి', ee: 'ీ', ii: 'ీ', u: 'ు', oo: 'ూ', uu: 'ూ', e: 'ె', ae: 'ే', ai: 'ై', o: 'ొ', oa: 'ో', au: 'ౌ' };
const TELUGU_CONSONANTS = { ksh: 'క్ష', chh: 'ఛ', kh: 'ఖ', gh: 'ఘ', ch: 'చ', jh: 'ఝ', th: 'థ', dh: 'ధ', ph: 'ఫ', bh: 'భ', sh: 'శ', ng: 'ఙ', ny: 'ఞ', nj: 'ఞ', k: 'క', g: 'గ', c: 'చ', j: 'జ', t: 'త', d: 'ద', n: 'న', p: 'ప', b: 'బ', m: 'మ', y: 'య', r: 'ర', l: 'ల', v: 'వ', w: 'వ', s: 'స', h: 'హ', f: 'ఫ', q: 'క', z: 'జ' };
const sortLongest = (map) => Object.keys(map).sort((a, b) => b.length - a.length);
const TELUGU_CONSONANT_KEYS = sortLongest(TELUGU_CONSONANTS);
const TELUGU_VOWEL_KEYS = sortLongest(TELUGU_VOWELS);
const transliterateTeluguWord = (raw) => {
  const dictionary = PHONETIC_WORDS.te[raw.toLowerCase()];
  if (dictionary) return dictionary;
  let output = ''; let pending = null; let i = 0;
  const flush = (virama) => { if (pending) { output += pending + (virama ? '్' : ''); pending = null; } };
  while (i < raw.length) {
    const rest = raw.slice(i).toLowerCase();
    const consonant = TELUGU_CONSONANT_KEYS.find((key) => rest.startsWith(key));
    if (consonant) { flush(true); pending = TELUGU_CONSONANTS[consonant]; i += consonant.length; continue; }
    const vowel = TELUGU_VOWEL_KEYS.find((key) => rest.startsWith(key));
    if (vowel) { if (pending) { output += pending + TELUGU_MATRAS[vowel]; pending = null; } else output += TELUGU_VOWELS[vowel]; i += vowel.length; continue; }
    flush(true); output += raw[i]; i += 1;
  }
  flush(true);
  // English past-tense/loanword endings are pronounced as Telugu డ్, not ద్.
  return output.replace(/తెద్$/, 'టెడ్').replace(/ెద్$/, 'ెడ్');
};
const phoneticConvert = (word, language) => {
  if (language === 'te') return transliterateTeluguWord(word);
  return PHONETIC_WORDS[language]?.[word.toLowerCase()] || word;
};
const phoneticPreview = (text, language) => {
  if (language === 'en') return '';
  const word = (text.match(/[A-Za-z]+$/) || [])[0];
  return word ? phoneticConvert(word, language) : '';
};
const commitPhoneticWords = (text, language, includeTrailing = false) => {
  if (language === 'en') return text;
  const boundary = includeTrailing ? '(?=\\s|[.,!?;:]|$)' : '(?=\\s|[.,!?;:])';
  return text.replace(new RegExp(`([A-Za-z]+)${boundary}`, 'g'), (word) => phoneticConvert(word, language));
};
const BusinessAdsManage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [followerCount, setFollowerCount] = useState(0);
  const [mediaUploads, setMediaUploads] = useState([]);
  const [fullImageUploads, setFullImageUploads] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [editPostId, setEditPostId] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [adLanguage, setAdLanguage] = useState('te');
  const [merchantProfile, setMerchantProfile] = useState(null);
  const [titleTouched, setTitleTouched] = useState(false);
  const [form, setForm] = useState({
    postType: 'offer',
    title: '',
    description: '',
    durationDays: 7,
    placements: ['shop_updates'],
    townName: '',
    businessName: '',
  });

  const authHeaders = () => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const apiRoot = () => String(API_BASE_URL).replace(/\/franchisee?\/?$/i, '').replace(/\/$/, '');
  const applyMerchantProfile = (profile) => {
    if (!profile) return;
    setMerchantProfile((current) => ({ ...current, ...profile }));
    setForm((current) => ({
      ...current,
      businessName: current.businessName || profile.business_name || profile.shop_name || profile.shopName || '',
      townName: current.townName || profile.city || profile.town || '',
    }));
  };
  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      // No tab filter — return all statuses (live/pending/paused/ended).
      const res = await axios.get(`${API_BASE_URL}/business-posts`, {
        params: { limit: 100 },
        headers: authHeaders(),
      });
      setPosts(res.data?.data || []);
      try {
        const analyticsRes = await axios.get(`${API_BASE_URL}/business-posts/analytics/summary`, {
          headers: authHeaders(),
        });
        setAnalytics(analyticsRes.data?.data || null);
      } catch {
        /* optional */
      }
      try {
        let businessId = '';
        try {
          const raw = localStorage.getItem('user');
          const user = raw ? JSON.parse(raw) : null;
          businessId = user?.id || user?.userId || '';
        } catch {
          businessId = '';
        }
        if (businessId) {
          const followersRes = await axios.get(
            `${API_BASE_URL}/shop-followers/business/${businessId}/count`,
            { headers: authHeaders() },
          );
          setFollowerCount(followersRes.data?.data?.followerCount || 0);
        }
      } catch {
        /* optional */
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'My Posts & Ads - LOCSY';
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    let active = true;
    // Use the authenticated session immediately, then refresh it from the
    // Vyapar profile endpoint. The fallback avoids an empty field on a slow
    // or path-prefixed API request.
    try {
      const raw = localStorage.getItem('user');
      if (raw) applyMerchantProfile(JSON.parse(raw));
    } catch (_) { /* Ignore malformed local session data. */ }
    axios.get(`${apiRoot()}/franchisee/admin/me`, { headers: authHeaders() })
      .then((response) => { if (active) applyMerchantProfile(response.data?.data); })
      .catch(() => { /* Local session fallback remains available. */ });
    return () => { active = false; };
  }, []);


  const categoryLabel = String(merchantProfile?.business_categories?.[0] || merchantProfile?.business_type || 'local business').replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  const postTypeLabel = POST_TYPES.find((item) => item.value === form.postType)?.label || 'Offer';
  const suggestedTitle = () => {
    const detail = form.description.trim().replace(/\s+/g, ' ').split(/[.!?]/)[0].slice(0, 72);
    const prefix = `${categoryLabel} ${postTypeLabel}`;
    return detail ? `${prefix} — ${detail}` : `${prefix} from ${form.businessName || 'your shop'}`;
  };
  useEffect(() => {
    if (!titleTouched) setForm((current) => ({ ...current, title: suggestedTitle() }));
  }, [form.description, form.postType, form.businessName, categoryLabel, titleTouched]);
  const fetchQuote = async (placements, durationDays) => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/business-posts/quote`,
        { placements, durationDays },
        { headers: authHeaders() },
      );
      setQuote(res.data?.data || null);
    } catch {
      setQuote(null);
    }
  };

  const uploadBusinessMedia = async (files, target = 'thumbnail') => {
    const selectedFiles = Array.from(files || []);
    if (!selectedFiles.length) return;

    setUploadingMedia(true);
    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API_BASE_URL}/business-posts/upload`,
          formData,
          {
            headers: authHeaders(),
          },
        );

        const uploaded = response.data?.data;
        if (uploaded?.url) {
          (target === 'full' ? setFullImageUploads : setMediaUploads)((current) => [...current, uploaded]);
        }
      }
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingMedia(false);
    }
  };

  const removeFullImageUpload = (index) => {
    setFullImageUploads((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const removeMediaUpload = (index) => {
    setMediaUploads((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  useEffect(() => {
    if (open) {
      fetchQuote(form.placements, form.durationDays);
    }
  }, [open, form.placements, form.durationDays]);

  const togglePlacement = (value) => {
    setForm((f) => {
      const has = f.placements.includes(value);
      let next = has ? f.placements.filter((p) => p !== value) : [...f.placements, value];
      if (value === 'shop_updates' && !has) {
        /* allow multiple */
      }
      if (!next.length) next = ['shop_updates'];
      return { ...f, placements: next };
    });
  };

  const scriptSafetyInstruction = adLanguage === 'te'
    ? 'For Telugu: do not invent or approximate long Telugu text in the image. Leave a clean high-contrast text-safe panel for the exact Telugu copy to be overlaid; only use supplied script when it can be reproduced exactly.'
    : adLanguage === 'hi'
      ? 'For Hindi: do not invent or approximate long Devanagari text in the image. Leave a clean high-contrast text-safe panel for the exact Hindi copy to be overlaid; only use supplied script when it can be reproduced exactly.'
      : '';
  const baseAiPrompt = [
    `Create a premium local-business advertisement poster for ${form.postType.replace(/_/g, ' ')}.`,
    form.title.trim() ? `Headline: ${form.title.trim()}.` : 'Add a clear, attractive headline.',
    form.description.trim() ? `Offer/details: ${form.description.trim()}.` : 'Use a concise, customer-friendly offer description.',
    form.businessName.trim() ? `Business name: ${form.businessName.trim()}.` : 'Leave a clean space for the business name.',
    form.townName.trim() ? `Location: ${form.townName.trim()}.` : '',
    'Use readable typography, high contrast, and an Indian local-business style. Do not add fake phone numbers, prices, watermarks, or unrelated logos.',
    scriptSafetyInstruction,
  ].filter(Boolean).join(' ');
  const selectedThumbnailSpec = form.placements.includes('popup') || form.placements.includes('my_town')
    ? { size: '1080×1920', ratio: '9:16 vertical', vertical: true }
    : form.placements.includes('home_pamphlets')
      ? { size: '1120×700', ratio: '16:10 landscape', vertical: false }
      : { size: '800×500', ratio: '16:10 landscape', vertical: false };
  const thumbnailAiPrompt = `${baseAiPrompt}\n\nThumbnail design rules (critical):\n- Exact canvas: ${selectedThumbnailSpec.size} pixels (${selectedThumbnailSpec.ratio})\n- Designed as a small Loksy citizen card thumbnail\n- ${selectedThumbnailSpec.vertical ? 'Use a vertical full-screen composition.' : 'Landscape only — do NOT generate or crop a vertical poster.'}\n- Keep shop name, offer and CTA inside generous safe margins; no edge text.\nOutput: one thumbnail only.`;
  const fullImageAiPrompt = `${baseAiPrompt}\n\nFull image design rules (critical):\n- Exact canvas: 1080×1920 pixels (9:16 vertical)\n- Must visually match the thumbnail: same colours, typography, offer and shop identity\n- Keep all important text inside safe margins.\nOutput: one full poster only.`;
  const copyAiPrompt = async (kind = 'thumbnail') => {
    try {
      await navigator.clipboard.writeText(kind === 'full' ? fullImageAiPrompt : thumbnailAiPrompt);
      toast.success(kind === 'full' ? 'Full-image AI prompt copied' : 'Thumbnail AI prompt copied');
    } catch {
      toast.error('Could not copy the AI prompt');
    }
  };
  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      const res = await axios.post(
        `${API_BASE_URL}/business-posts`,
        {
          postType: form.postType,
          title: form.title.trim(),
          description: form.description,
          media: [
            ...mediaUploads.map((item) => ({ ...item, role: 'thumbnail' })),
            ...fullImageUploads.map((item) => ({ ...item, role: 'full' })),
          ],
          placements: form.placements,
          durationDays: form.durationDays,
          townName: form.townName || undefined,
          businessName: form.businessName || undefined,
        },
        { headers: authHeaders() },
      );
      const post = res.data?.data;
      toast.success('Post created');
      setOpen(false);
      setMediaUploads([]);
      setFullImageUploads([]);
      loadPosts();

      if (post?.paymentStatus === 'pending' && post?.amountInr > 0) {
        toast.info(`Payment required: ₹${post.amountInr}. Use Pay Now when ready.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Create failed');
    }
  };

  const handleEditClick = (post) => {
    setForm({
      postType: post.postType,
      title: post.title,
      description: post.description || '',
      durationDays: post.durationDays,
      placements: post.placements,
      townName: post.townName || '',
      businessName: post.businessName || '',
    });
    const media = post.media || [];
    setMediaUploads(media.filter((item) => String(item?.role || '').toLowerCase() !== 'full'));
    setFullImageUploads(media.filter((item) => String(item?.role || '').toLowerCase() === 'full'));
    setEditPostId(post.id);
    setOpen(true);
  };

  const handleUpdate = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      await axios.put(
        `${API_BASE_URL}/business-posts/${editPostId}`,
        {
          postType: form.postType,
          title: form.title.trim(),
          description: form.description,
          media: [
            ...mediaUploads.map((item) => ({ ...item, role: 'thumbnail' })),
            ...fullImageUploads.map((item) => ({ ...item, role: 'full' })),
          ],
          townName: form.townName || undefined,
          businessName: form.businessName || undefined,
        },
        { headers: authHeaders() },
      );
      toast.success('Post updated');
      setOpen(false);
      setEditPostId(null);
      setMediaUploads([]);
      setFullImageUploads([]);
      loadPosts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/business-posts/${postId}`, {
        headers: authHeaders(),
      });
      toast.success('Post deleted');
      loadPosts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditPostId(null);
    setMediaUploads([]);
    setFullImageUploads([]);
    setWizardStep(0);
    setAdLanguage('te');
    setTitleTouched(false);
    setForm({
      postType: 'offer',
      title: '',
      description: '',
      durationDays: 7,
      placements: ['shop_updates'],
      townName: '',
      businessName: '',
    });
  };

const updateText = (key, value) => {
    const phoneticField = ['title', 'description', 'businessName', 'townName'].includes(key);
    const committed = phoneticField ? commitPhoneticWords(value, adLanguage) : value;
    setForm((current) => ({ ...current, [key]: committed }));
  };
  const dictateInto = (key) => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) { toast.info('Voice typing is not supported by this browser. Use the device keyboard microphone.'); return; }
    const recognition = new Recognition();
    recognition.lang = adLanguage === 'te' ? 'te-IN' : adLanguage === 'hi' ? 'hi-IN' : 'en-IN';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => updateText(key, `${form[key] ? `${form[key]} ` : ''}${event.results[0][0].transcript}`);
    recognition.onerror = () => toast.error('Voice typing could not start. Check microphone permission.');
    recognition.start();
  };
  const commitPhonetic = (key) => {
    const preview = phoneticPreview(form[key] || '', adLanguage);
    if (preview) setForm((current) => ({ ...current, [key]: current[key].replace(/[A-Za-z]+$/, preview) }));
  };
  const nextWizardStep = () => {
    if (wizardStep === 0 && !form.title.trim()) { toast.error('Title is required'); return; }
    setWizardStep((step) => Math.min(step + 1, 3));
  };
  const loadMyAnalytics = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/business-posts/analytics/summary`, {
        headers: authHeaders(),
      });
      setAnalytics(res.data?.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Analytics failed');
    }
  };

  const startPayment = async (postId) => {
    try {
      const orderRes = await axios.post(
        `${API_BASE_URL}/business-posts/${postId}/payment/order`,
        {},
        { headers: authHeaders() },
      );
      const { order, keyId } = orderRes.data;
      if (!window.Razorpay) {
        toast.error('Razorpay script not loaded');
        return;
      }
      const rzp = new window.Razorpay({
        key: keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.id,
        handler: async (response) => {
          await axios.post(
            `${API_BASE_URL}/business-posts/${postId}/payment/verify`,
            response,
            { headers: authHeaders() },
          );
          toast.success('Payment successful');
          loadPosts();
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>Ads</Typography>
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, mb: 1, scrollSnapType: 'x mandatory' }}>
        <Card onClick={() => { setEditPostId(null); setMediaUploads([]); setOpen(true); }} sx={{ minWidth: { xs: 220, sm: 260 }, cursor: 'pointer', scrollSnapAlign: 'start', border: '1px solid', borderColor: 'primary.light', background: 'linear-gradient(135deg, #eef2ff, #fff)' }}>
          <CardContent><CampaignIcon color="primary" sx={{ fontSize: 44, mb: 1 }} /><Typography fontWeight={800}>Create ads</Typography><Typography variant="body2" color="text.secondary">Image, video and text notices in one studio</Typography></CardContent>
        </Card>
        <Card onClick={() => document.getElementById('my-ads')?.scrollIntoView({ behavior: 'smooth' })} sx={{ minWidth: { xs: 220, sm: 260 }, cursor: 'pointer', scrollSnapAlign: 'start', border: '1px solid', borderColor: 'divider', background: 'linear-gradient(135deg, #f0fdf4, #fff)' }}>
          <CardContent><CarouselIcon color="success" sx={{ fontSize: 44, mb: 1 }} /><Typography fontWeight={800}>My Ads</Typography><Typography variant="body2" color="text.secondary">Live, pending, pay and manage published ads</Typography></CardContent>
        </Card>
      </Box>
      <Card sx={{ mb: 3, borderRadius: 3, bgcolor: '#f0fdf4' }}><CardContent>
        <Typography fontWeight={800}>Promote your business or idea</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>Choose an offer or idea, create your ad, and publish it on Loksy.</Typography>
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', mt: 2, pb: .5 }}>
          {FLUTTER_AD_TYPES.map(([title, , postType]) => <Chip key={title} label={title} onClick={() => { setForm((f) => ({ ...f, postType })); setEditPostId(null); setMediaUploads([]); setOpen(true); }} clickable color="primary" variant="outlined" />)}
        </Box>
      </CardContent></Card>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={800}>
          My Posts & Promotions
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<StatsIcon />} variant="outlined" onClick={loadMyAnalytics}>
            My analytics
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={loadPosts}>
            Refresh
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => {
              setMediaUploads([]);
              setOpen(true);
            }}
          >
            Create post
          </Button>
        </Stack>
      </Stack>

      {(analytics || followerCount > 0) && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={analytics ? () => setAnalytics(null) : undefined}
        >
          {analytics
            ? `Views: ${analytics.totalViews} | Reach: ${analytics.uniqueViews} | Shares: ${
                analytics.totalShares ?? 0
              } | CTR: ${analytics.ctr}% | `
            : ''}
          Followers: {followerCount}
          {followerCount > 0
            ? ' — live posts notify followers automatically'
            : ' — citizens can Follow your shop for update alerts'}
        </Alert>
      )}

      <Box id="my-ads">
        {loading ? (
        <Typography>Loading…</Typography>
      ) : posts.length === 0 ? (
        <Alert severity="info">No posts yet. Create your first post for shop updates or paid placements.</Alert>
      ) : (
        <Grid container spacing={2}>
          {posts.map((p) => (
            <Grid item xs={12} md={6} key={p.id}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography fontWeight={700}>{p.title}</Typography>
                    <Chip size="small" label={p.status} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {p.postType} · ₹{p.amountInr}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Views {p.totalViews ?? 0} · Shares {p.totalShares ?? 0} · Reach{' '}
                    {p.uniqueViews ?? 0} · CTR {p.ctr ?? 0}%
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {(p.placements || []).map((pl) => (
                      <Chip key={pl} label={pl} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
                    {p.paymentStatus === 'pending' && p.amountInr > 0 && (
                      <Button size="small" variant="contained" onClick={() => startPayment(p.id)} sx={{ mr: 'auto' }}>
                        Pay now (₹{p.amountInr})
                      </Button>
                    )}
                    <Button size="small" variant="outlined" onClick={() => handleEditClick(p)}>
                      Edit
                    </Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(p.id)}>
                      Delete
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      </Box>

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editPostId ? 'Edit post' : 'Create advertisement'}</DialogTitle>
        <DialogContent>
          {!editPostId && <MobileStepper variant="progress" steps={4} position="static" activeStep={wizardStep} nextButton={<span />} backButton={<span />} sx={{ mb: 2, px: 0 }} />}
          {(() => {
            const stepTitles = ['Details', 'Thumbnail creative', 'Full-image creative', 'Visibility & publish'];
            const title = editPostId ? 'Update advertisement' : stepTitles[wizardStep];
            return <Box sx={{ mb: 2 }}><Typography variant="h6" fontWeight={800}>{title}</Typography><Typography variant="body2" color="text.secondary">{editPostId ? 'Update your existing creative.' : `Step ${wizardStep + 1} of 4`}</Typography></Box>;
          })()}
          <Stack spacing={2}>
            {(editPostId || wizardStep === 0) && <>
              <FormControl fullWidth><InputLabel>Post type</InputLabel><Select value={form.postType} label="Post type" onChange={(e) => updateText('postType', e.target.value)}>{POST_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}</Select></FormControl>
              <Box><Typography fontWeight={700} sx={{ mb: 1 }}>Ad image language</Typography><Stack direction="row" spacing={1}>{[['te','TE · తెలుగు'],['en','EN · English'],['hi','HI · हिंदी']].map(([code,label]) => <Chip key={code} label={label} clickable color={adLanguage === code ? 'primary' : 'default'} variant={adLanguage === code ? 'filled' : 'outlined'} onClick={() => setAdLanguage(code)} />)}</Stack></Box>
              {adLanguage !== 'en' && <Alert severity="info">{adLanguage === 'te' ? 'Telugu' : 'Hindi'} phonetic keyboard ON — type English, then tap its script preview to insert it.</Alert>}
              <TextField label="Title" fullWidth value={form.title} onChange={(e) => { setTitleTouched(true); updateText('title', e.target.value); }} onBlur={() => setForm((current) => ({ ...current, title: commitPhoneticWords(current.title, adLanguage, true) }))} helperText={`Suggested from ${categoryLabel} category and your description`} InputProps={{ endAdornment: <IconButton size="small" onClick={() => dictateInto('title')} aria-label="Dictate title"><MicIcon fontSize="small" /></IconButton> }} />
              <TextField label="Description" fullWidth multiline rows={3} value={form.description} onChange={(e) => updateText('description', e.target.value)} onBlur={() => setForm((current) => ({ ...current, description: commitPhoneticWords(current.description, adLanguage, true) }))} InputProps={{ endAdornment: <IconButton size="small" onClick={() => dictateInto('description')} aria-label="Dictate description"><MicIcon fontSize="small" /></IconButton> }} />
              <TextField label="Business name" fullWidth value={form.businessName} helperText="Fetched from your registered Vyapar profile; you can change it for this ad." onChange={(e) => updateText('businessName', e.target.value)} onBlur={() => setForm((current) => ({ ...current, businessName: commitPhoneticWords(current.businessName, adLanguage, true) }))} InputProps={{ endAdornment: <IconButton size="small" onClick={() => dictateInto('businessName')} aria-label="Dictate business name"><MicIcon fontSize="small" /></IconButton> }} />
              <TextField label="Town (optional)" fullWidth value={form.townName} onChange={(e) => updateText('townName', e.target.value)} onBlur={() => setForm((current) => ({ ...current, townName: commitPhoneticWords(current.townName, adLanguage, true) }))} InputProps={{ endAdornment: <IconButton size="small" onClick={() => dictateInto('townName')} aria-label="Dictate town"><MicIcon fontSize="small" /></IconButton> }} />
              {['title', 'description', 'businessName', 'townName'].map((key) => { const preview = phoneticPreview(form[key] || '', adLanguage); return preview ? <Button key={key} size="small" onClick={() => commitPhonetic(key)}>Insert {adLanguage === 'te' ? 'తెలుగు' : 'हिंदी'}: {preview}</Button> : null; })}
            </>}
            {!editPostId && wizardStep === 1 && <>
              <Alert severity="info">Thumbnail is shown first in Loksy home and shop rails. Upload a landscape image where possible.</Alert>
              <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'primary.light' }}><Typography fontWeight={800}><AutoAwesomeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: .5 }} />Thumbnail AI prompt</Typography><Typography variant="caption" color="text.secondary">{`Exact ${selectedThumbnailSpec.size} (${selectedThumbnailSpec.ratio}) canvas — this prompt prevents a portrait poster for a landscape card.`}</Typography><Stack direction="row" spacing={1} sx={{ mt: 1 }}><Button size="small" variant="contained" startIcon={<ContentCopyIcon />} onClick={copyAiPrompt}>Copy prompt</Button><Button size="small" component="a" href="https://gemini.google.com/" target="_blank" rel="noreferrer">Gemini</Button><Button size="small" component="a" href="https://chatgpt.com/" target="_blank" rel="noreferrer">ChatGPT</Button></Stack></Paper>
              <Box><Button variant="outlined" component="label" disabled={uploadingMedia}>{uploadingMedia ? 'Uploading thumbnail…' : 'Upload thumbnail'}<input hidden type="file" accept="image/png,image/jpeg,image/jpg,image/webp" multiple onChange={(e) => { uploadBusinessMedia(e.target.files, 'thumbnail'); e.target.value = ''; }} /></Button><Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>The first thumbnail is the card image shown to citizens.</Typography>{mediaUploads.length > 0 && <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto' }}>{mediaUploads.map((item, index) => <Paper key={`${item.url}-${index}`} variant="outlined" sx={{ p: 1, minWidth: 130 }}><img src={item.url} alt="Thumbnail upload" style={{ width: 110, height: 80, objectFit: 'cover' }} /><Button size="small" onClick={() => removeMediaUpload(index)}>Remove</Button></Paper>)}</Stack>}</Box>
            </>}
            {!editPostId && wizardStep === 2 && <>
              <Alert severity="info">Full image opens after the thumbnail. Use a clear poster; vertical 1080×1920 works for popup and reels.</Alert>
              <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'primary.light' }}><Typography fontWeight={800}><AutoAwesomeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: .5 }} />Full-image AI prompt</Typography><Typography variant="caption" color="text.secondary">Copy the same campaign prompt for a detailed full poster, then upload it here.</Typography><Stack direction="row" spacing={1} sx={{ mt: 1 }}><Button size="small" variant="contained" startIcon={<ContentCopyIcon />} onClick={() => copyAiPrompt('full')}>Copy prompt</Button><Button size="small" component="a" href="https://gemini.google.com/" target="_blank" rel="noreferrer">Gemini</Button><Button size="small" component="a" href="https://chatgpt.com/" target="_blank" rel="noreferrer">ChatGPT</Button></Stack></Paper>
              <Box><Button variant="outlined" component="label" disabled={uploadingMedia}>{uploadingMedia ? 'Uploading full image…' : 'Upload full image'}<input hidden type="file" accept="image/png,image/jpeg,image/jpg,image/webp" multiple onChange={(e) => { uploadBusinessMedia(e.target.files, 'full'); e.target.value = ''; }} /></Button>{fullImageUploads.length > 0 && <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto' }}>{fullImageUploads.map((item, index) => <Paper key={`${item.url}-${index}`} variant="outlined" sx={{ p: 1, minWidth: 130 }}><img src={item.url} alt="Full advertisement upload" style={{ width: 110, height: 160, objectFit: 'cover' }} /><Button size="small" onClick={() => removeFullImageUpload(index)}>Remove</Button></Paper>)}</Stack>}</Box>
            </>}
            {!editPostId && wizardStep === 3 && <>
              <TextField type="number" label="Promotion days (paid placements)" fullWidth value={form.durationDays} onChange={(e) => updateText('durationDays', Number(e.target.value))} />
              <Typography variant="subtitle2">Visibility</Typography>{PLACEMENTS.map((pl) => <FormControlLabel key={pl.value} control={<Checkbox checked={form.placements.includes(pl.value)} onChange={() => togglePlacement(pl.value)} />} label={<Box><Typography variant="body2" fontWeight={600}>{pl.label}</Typography><Typography variant="caption" color="text.secondary">{pl.hint}</Typography></Box>} />)}
              <Paper variant="outlined" sx={{ p: 2 }}><Typography fontWeight={700}>{form.title || 'Your advertisement'}</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Thumbnail: {mediaUploads.length} · Full images: {fullImageUploads.length}</Typography>{quote && <Typography fontWeight={700} sx={{ mt: 1 }}>Estimated: ₹{quote.amountInr}</Typography>}</Paper>
            </>}
            {editPostId && <Alert severity="info">Placements and promotion duration cannot be edited once the post is created.</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>{!editPostId && wizardStep > 0 && <Button onClick={() => setWizardStep((step) => step - 1)}>Back</Button>}<Button onClick={handleCloseDialog}>Cancel</Button>{editPostId ? <Button variant="contained" onClick={handleUpdate}>Save changes</Button> : wizardStep < 3 ? <Button variant="contained" onClick={nextWizardStep}>Next</Button> : <Button variant="contained" onClick={handleCreate}>Publish</Button>}</DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessAdsManage;
